// Metriche di qualità dell'aria rilevate dal pedale UGO.
// Condivise fra gli Open Data (OpenDataAirQuality) e la pagina "Dati ambientali"
// di una sessione (SessionEnvironment): stesse grandezze, stessa palette, stesse soglie.

export const airMetricConfig = {
  ozone_density: {
    key: "ozone_density",
    titleTranslationKey: "ozoneDensityMetricTitle",
    descriptionTranslationKey: "ozoneDensityMetricDescription",
    symbol: "ppb",
  },
  aqi_epa: {
    key: "aqi_epa",
    titleTranslationKey: "aqiEpaMetricTitle",
    descriptionTranslationKey: "aqiEpaMetricDescription",
    symbol: "",
  },
  pm1: {
    key: "pm1",
    titleTranslationKey: "pm1MetricTitle",
    descriptionTranslationKey: "pm1MetricDescription",
    symbol: "µg/m³",
  },
  pm2_5: {
    key: "pm2_5",
    titleTranslationKey: "pm2_5MetricTitle",
    descriptionTranslationKey: "pm2_5MetricDescription",
    symbol: "µg/m³",
  },
  pm10: {
    key: "pm10",
    titleTranslationKey: "pm10MetricTitle",
    descriptionTranslationKey: "pm10MetricDescription",
    symbol: "µg/m³",
  },
};

export const airMetricOrder = ["ozone_density", "aqi_epa", "pm1", "pm2_5", "pm10"];

/**
 * colori dei tab (bottoni metriche)
 */
export const TAB_COLORS = [
  "#2e7d32",
  "#ff7e00",
  "#0277bd",
  "#4e342e",
  "#00796b",
];

/**
 * palette colori per colorare la mappa (tipo AQI)
 */
export const METRIC_PALETTE = [
  "#00e400", // green
  "#ffff00", // yellow
  "#ff7e00", // orange
  "#ff0000", // red
  "#8f3f97", // purple
  "#7e0023", // maroon
];

export const NO_DATA_COLOR = "#9e9e9e";

// Soglie del particolato: breakpoint AQI dell'EPA (media 24h, µg/m³).
// PM2.5 secondo la revisione 2024; PM1 non ha una scala ufficiale e riusa quella del PM2.5,
// di cui è un sottoinsieme.
const PM_FINE_THRESHOLDS = [
  { min: 0, max: 9, color: METRIC_PALETTE[0] },
  { min: 10, max: 35, color: METRIC_PALETTE[1] },
  { min: 36, max: 55, color: METRIC_PALETTE[2] },
  { min: 56, max: 125, color: METRIC_PALETTE[3] },
  { min: 126, max: 225, color: METRIC_PALETTE[4] },
  { min: 226, max: 325, color: METRIC_PALETTE[5] },
];

export const METRIC_THRESHOLDS = {
  aqi_epa: [
    { min: 0, max: 50, color: METRIC_PALETTE[0] }, // green
    { min: 51, max: 100, color: METRIC_PALETTE[1] }, // yellow
    { min: 101, max: 150, color: METRIC_PALETTE[2] }, // orange
    { min: 151, max: 200, color: METRIC_PALETTE[3] }, // red
    { min: 201, max: 300, color: METRIC_PALETTE[4] }, // purple
    { min: 301, max: 500, color: METRIC_PALETTE[5] }, // maroon
  ],

  pm1: PM_FINE_THRESHOLDS,
  pm2_5: PM_FINE_THRESHOLDS,
  pm10: [
    { min: 0, max: 54, color: METRIC_PALETTE[0] },
    { min: 55, max: 154, color: METRIC_PALETTE[1] },
    { min: 155, max: 254, color: METRIC_PALETTE[2] },
    { min: 255, max: 354, color: METRIC_PALETTE[3] },
    { min: 355, max: 424, color: METRIC_PALETTE[4] },
    { min: 425, max: 604, color: METRIC_PALETTE[5] },
  ],

  // Ozono (colonna O3)
  ozone_density: [
    { min: 0, max: 62, color: METRIC_PALETTE[0] },
    { min: 63, max: 124, color: METRIC_PALETTE[1] },
    { min: 125, max: 164, color: METRIC_PALETTE[2] },
    { min: 165, max: 204, color: METRIC_PALETTE[3] },
    { min: 205, max: 404, color: METRIC_PALETTE[4] },
    { min: 405, max: 604, color: METRIC_PALETTE[5] },
  ],
};

export function getMetricColor(metricKey, value, min, max) {
  // Nessun dato → grigio
  if (value == null || Number.isNaN(value)) return NO_DATA_COLOR;

  // Se ho soglie predefinite per questa metrica, uso quelle.
  // Le soglie sono contigue e ordinate: un valore cade nella prima fascia il cui
  // massimo lo supera, così anche un valore non intero fra due fasce (es. 50,5)
  // prende la fascia successiva e non finisce fuori scala.
  const thresholds = METRIC_THRESHOLDS[metricKey];
  if (thresholds) {
    for (const { max: tMax, color } of thresholds) {
      if (value <= tMax) {
        return color;
      }
    }
    // Fuori scala in alto → ultimo colore (maroon)
    return thresholds[thresholds.length - 1].color;
  }

  // Fallback: vecchia logica min/max (per eventuali metriche future)
  if (min == null || max == null || min === max) {
    return METRIC_PALETTE[Math.floor(METRIC_PALETTE.length / 2)];
  }

  const range = max - min;
  if (range <= 0) {
    return METRIC_PALETTE[Math.floor(METRIC_PALETTE.length / 2)];
  }

  const step = range / METRIC_PALETTE.length;
  const idx = Math.min(
    METRIC_PALETTE.length - 1,
    Math.max(0, Math.floor((value - min) / step))
  );
  return METRIC_PALETTE[idx];
}

export function normalizeMetricValue(metricKey, raw) {
  if (raw == null || raw === "") return null;
  const num = Number(raw);
  if (Number.isNaN(num)) return null;

  // SOLO per ozone_density: il sensore misura in centesimi di ppb
  if (metricKey === "ozone_density") return num / 100;

  // Il dispositivo manda l'AQI dimezzato: la guida GreenBari dice di moltiplicarlo per 2, e solo
  // cosi' il valore sta sulla scala EPA 0-500 delle fasce di colore. Le medie sono lineari, quindi
  // raddoppiare qui vale anche per le medie giornaliere degli Open Data, senza toccare backend e DB.
  if (metricKey === "aqi_epa") return num * 2;

  return num;
}

/** "#rrggbb" → [r, g, b] */
export function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

/** interpolazione lineare fra due colori [r,g,b], t in [0,1] */
export function lerpRgb(a, b, t) {
  const k = Math.min(1, Math.max(0, t));
  return [
    Math.round(a[0] + (b[0] - a[0]) * k),
    Math.round(a[1] + (b[1] - a[1]) * k),
    Math.round(a[2] + (b[2] - a[2]) * k),
  ];
}
